import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js'
import axios, { AxiosRequestConfig } from 'axios'
import { oktaAuth as moduleOktaAuth } from '../authentication/OktaSSO'
import oktaService from './oktaService'
import { getAccessTokenValidity, TokenStateKind } from './tokenValidity'
import { logDiagnosticEvent } from './diagnosticLogger'

export type AuthenticationRequirement = (config: AxiosRequestConfig) => boolean

function getUserId(auth: OktaAuth): string | undefined {
  return auth.tokenManager?.getTokensSync?.().idToken?.claims?.preferred_username
}

export async function getRequestToken(
  auth: OktaAuth,
  config: AxiosRequestConfig,
  requiresAuthentication: AuthenticationRequirement = () => true,
): Promise<string | undefined> {
  const validity = getAccessTokenValidity(auth.tokenManager)

  if (!requiresAuthentication(config)) {
    return validity.token?.accessToken
  }

  // ProtectedRoute owns the initial silent token acquisition path. A missing token
  // must not redirect to Portal while that attempt is still in progress.
  if (validity.status === TokenStateKind.Missing) {
    return Promise.reject(oktaService.tokenValidationError())
  }

  if (validity.status === TokenStateKind.Expired) {
    const userId = getUserId(auth)

    // If ProtectedRoute is still mid-getWithoutPrompt(), the token may be about
    // to be refreshed silently — do not redirect prematurely.
    if (oktaService.silentAcquisitionInProgress) {
      logDiagnosticEvent({
        eventType: 'concurrent_acquisition_blocked',
        userId,
        outcome: 'blocked',
        level: 'warn',
      })
      return Promise.reject(oktaService.tokenValidationError())
    }

    logDiagnosticEvent({ eventType: 'token_renewal_attempted', userId, level: 'info' })
    logDiagnosticEvent({
      eventType: 'token_renewal_failed',
      userId,
      outcome: 'expired',
      level: 'warn',
    })

    logDiagnosticEvent({
      eventType: 'redirect_to_okta',
      userId,
      outcome: 'expired_token',
      level: 'warn',
    })
    // Route through the centralized guard to prevent PKCE race conditions.
    oktaService.triggerOktaRedirect(toRelativeUrl(window.location.href, window.location.origin))
    return Promise.reject(oktaService.tokenValidationError())
  }

  return validity.token?.accessToken
}

export function handleProtectedUnauthorized(
  error: unknown,
  requiresAuthentication: AuthenticationRequirement = () => true,
): void {
  if (isProtectedUnauthorized(error, requiresAuthentication)) {
    const userId = getUserId(moduleOktaAuth)

    logDiagnosticEvent({
      eventType: 'redirect_to_okta',
      userId,
      outcome: '401',
      level: 'warn',
    })
    // Route through the centralized guard to prevent PKCE race conditions.
    oktaService.triggerOktaRedirect(toRelativeUrl(window.location.href, window.location.origin))
  }
}

function isProtectedUnauthorized(
  error: unknown,
  requiresAuthentication: AuthenticationRequirement,
): boolean {
  if (!axios.isAxiosError(error) || error.response?.status !== 401) {
    return false
  }

  return requiresAuthentication(error.config)
}
